import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import CreateJobModal from '../../components/CreateJobModal'
import DuplicateJobModal from '../../components/DuplicateJobModal'
import PreReserveDialog from './PreReserveDialog'
import SyncPreflightModal from '../../components/SyncPreflightModal'
import BoxtopReadbackDialog from '../../components/BoxtopReadbackDialog'
import PortCodeProblemDialog, { type PortProblem } from '../../components/PortCodeProblemDialog'
import MissingBoxtopAccountDialog, { type MissingAccountParty } from '../../components/MissingBoxtopAccountDialog'
import type { BoxtopReadback } from '../../lib/boxtopReadback'
import { useSyncToBoxtrax } from '../../hooks/useSyncToBoxtrax'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'
import NewGroupDialog from '../../components/Group/NewGroupDialog'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs, type ColDef } from '../Warehouse/useColumnPrefs'
import { SortableColHeader, ColumnToggleButton } from '../Warehouse/ColDragHeader'
import { hasAnyRole } from '../../lib/roles'
import { formatRelativeTime } from '../../lib/relativeTime'
import { usePagination } from '../../hooks/usePagination'
import PaginationControls from '../../components/PaginationControls'
import { getCachedCount, setCachedCount } from '../../lib/countCache'

const STATUSES = [
  { value: 'PRE', label: 'Pre-Alert' },
  { value: 'CUS', label: 'Custom' },
  { value: 'CTR', label: 'Container' },
  { value: 'DEL', label: 'Delivered' },
  { value: 'PDY', label: 'Pending Delivery' },
  { value: 'POD', label: 'Proof of Delivery' },
  { value: 'INV', label: 'Invoiced' },
  { value: 'WAR', label: 'Warehouse' },
  { value: 'RET', label: 'Return' },
  { value: 'COM', label: 'Completed' },
  { value: 'PRO', label: 'Processing' },
  { value: 'CAN', label: 'Cancelled' },
  { value: 'DEF', label: 'Default' },
  { value: 'WKD', label: 'Worked' },
  { value: 'EXP', label: 'Expired' },
  { value: 'SDI', label: 'Standby Delivery Item' },
  { value: 'REQ', label: 'Requested' }
] as const

// Y3: top filter groups. "Domestic"/"Cross Trade" aren't `direction` values (see
// migration 20260809e) -- Domestic reuses the existing transport_mode='DOMESTIC',
// Cross Trade reads the new is_cross_trade flag; both are handled specially in
// loadJobs() below rather than a plain .eq('direction', ...).
const SCOPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'mine', label: 'My Jobs' },
  { value: 'pre', label: 'Pre-Jobs' },
] as const

const DIRECTION_GROUP_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'IMP', label: 'Import' },
  { value: 'EXP', label: 'Export' },
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'CROSS_TRADE', label: 'Cross Trade' },
] as const

const MODE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'AIR', label: 'Air' },
  { value: 'SEA', label: 'Sea' },
  { value: 'ROAD', label: 'Road' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'COURIER', label: 'Courier' },
] as const

const INVOICE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'not_invoiced', label: 'Not Invoiced' },
  { value: 'open', label: 'Open Jobs' },
] as const

// Y3/Y4: BoxTop-style column set. Default visible subset per Gary: Job Number,
// Customer, Origin Port, Destination Port, Terms, ETA -- the rest are available via
// the gear icon and persisted per-user in user_preferences (useColumnPrefs, already
// used the same way by the Warehouse module's list pages).
const ALL_JOBS_COL_DEFS: readonly ColDef[] = [
  { id: 'job_ref', label: 'Job Number' },
  { id: 'customer_name', label: 'Customer' },
  { id: 'origin_port', label: 'Origin Port' },
  { id: 'destination_port', label: 'Destination Port' },
  { id: 'terms', label: 'Terms' },
  { id: 'eta', label: 'ETA' },
  { id: 'job_date', label: 'Job Date' },
  { id: 'service_type', label: 'Service Type' },
  { id: 'master_job', label: 'Master Job' },
  { id: 'customer_ref', label: 'Customer Ref' },
  { id: 'boxtrax_ref', label: 'BoxTop Ref' },
  { id: 'job_direction', label: 'Job Type' },
  { id: 'transport_mode', label: 'Mode' },
  // Z71: renamed from "AWB Master" -- short/generic since this column shows either an air
  // MAWB or a sea MBL depending on transport_mode (see awbMaster computation below).
  { id: 'awb_master', label: 'Master' },
  { id: 'arrival_date', label: 'Arrival Date' },
  { id: 'complete', label: 'Complete' },
  { id: 'shipper_name', label: 'Shipper Name' },
  { id: 'consignee_name', label: 'Consignee Name' },
  { id: 'nop', label: 'NOP' },
  { id: 'weight', label: 'Weight' },
  { id: 'chargeable_weight', label: 'Chargeable Weight' },
  { id: 'status', label: 'Status' },
  { id: 'sync_status', label: 'Sync Status' },
] as const
// BoxTop sync is off: its two columns are not offered (the data stays in the table, see lib/boxtopSync.ts).
const JOBS_COL_DEFS: readonly ColDef[] = BOXTOP_SYNC_ENABLED
  ? ALL_JOBS_COL_DEFS
  : ALL_JOBS_COL_DEFS.filter(c => c.id !== 'boxtrax_ref' && c.id !== 'sync_status')
// Y-followup (task 22, bug E): BoxTop Ref shows by default -- it's the field Gary
// cross-checks against BoxTop directly, unlike the rest of this default-hidden set.
const DEFAULT_VISIBLE_JOBS_COLS = ['job_ref', 'customer_name', 'origin_port', 'destination_port', 'terms', 'eta', ...(BOXTOP_SYNC_ENABLED ? ['boxtrax_ref'] : [])]
const DEFAULT_HIDDEN_JOBS_COLS = JOBS_COL_DEFS.map(c => c.id).filter(id => !DEFAULT_VISIBLE_JOBS_COLS.includes(id))

const JOBS_SELECT = `
  *,
  importer:customers!importer_id(name),
  consignor:customers!consignor_id(name),
  hbl_importer:customers!hbl_importer_id(name),
  hbl_consignor:customers!hbl_consignor_id(name),
  invoice_to:customers!invoice_to_id(name),
  parent:jobs!parent_job_id(job_ref),
  job_sea_details(
    master_bl_no, arrival_date,
    shipping_terms:shipping_terms!shipping_terms_id(code),
    departure_port:ports!departure_port_id(name, code),
    arrival_port:ports!arrival_port_id(name, code)
  ),
  job_air_details(
    mawb_prefix, mawb_no, arrival_date, actual_arrival_date,
    shipping_terms:shipping_terms!shipping_terms_id(code),
    departure_port:ports!departure_airport_id(name, code),
    arrival_port:ports!arrival_airport_id(name, code)
  ),
  job_goods(no_of_packs, gross_weight, chargeable_weight_hawb, chargeable_weight_mawb)
`

// PostgREST embeds a 1:1 relationship (job_sea_details.job_id is UNIQUE) as a single
// object, but this guards against it coming back as a one-item array regardless.
function firstOrSelf(x: any): any {
  return Array.isArray(x) ? x[0] : x
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB')
}

function fmtPort(p: { name?: string | null; code?: string | null } | null | undefined) {
  if (!p?.name) return '—'
  return p.code ? `${p.name} (${p.code})` : p.name
}

// Z9-fix2: universal search. jobs' own columns (job_ref/client_ref/boxtrax_ref) can be
// matched with a single .or(), but the rest -- customer names, BL/AWB numbers, vessel,
// container numbers, and the parent job's ref for child bills -- live on joined tables
// that PostgREST can't OR together with the base table in one request. Resolve each in
// parallel, union the matching job ids, then filter the main query with .in('id', ...).
function escapeIlikeTerm(term: string): string {
  return term.trim().replace(/[%_,()]/g, '')
}

async function searchMatchingJobIds(rawTerm: string): Promise<string[]> {
  const cleaned = escapeIlikeTerm(rawTerm)
  if (!cleaned) return []
  const t = `%${cleaned}%`
  const idSet = new Set<string>()

  const [directRes, masterRes, custRes, seaRes, airRes, containerRes] = await Promise.all([
    supabase.from('jobs').select('id').or(`job_ref.ilike.${t},client_ref.ilike.${t},boxtrax_ref.ilike.${t}`),
    // master_job_ref: jobs whose PARENT's job_ref matches the term (e.g. searching
    // "KI26034" should surface child bill "KI26034-15").
    supabase.from('jobs').select('id').ilike('job_ref', t),
    // Z88: capped -- a common term can match thousands of customers, and every id gets pasted into
    // the .or() URL below. First 50 name matches is the deliberate limit.
    supabase.from('customers').select('id').ilike('name', t).order('name').limit(50),
    supabase.from('job_sea_details').select('job_id').or(`master_bl_no.ilike.${t},house_bl_no.ilike.${t},vessel_name.ilike.${t},voyage_no.ilike.${t}`),
    supabase.from('job_air_details').select('job_id').or(`mawb_no.ilike.${t},hawb_no.ilike.${t}`),
    supabase.from('job_containers').select('job_id').ilike('container_no', t),
  ])

  directRes.data?.forEach((j: { id: string }) => idSet.add(j.id))
  seaRes.data?.forEach((s: { job_id: string }) => idSet.add(s.job_id))
  airRes.data?.forEach((a: { job_id: string }) => idSet.add(a.job_id))
  containerRes.data?.forEach((c: { job_id: string }) => idSet.add(c.job_id))

  const masterIds = (masterRes.data || []).map((m: { id: string }) => m.id)
  const custIds = (custRes.data || []).map((c: { id: string }) => c.id)

  const [childrenRes, custJobsRes] = await Promise.all([
    masterIds.length > 0
      ? supabase.from('jobs').select('id').in('parent_job_id', masterIds)
      : Promise.resolve({ data: [] as { id: string }[] }),
    custIds.length > 0
      ? supabase.from('jobs').select('id').or(
          `importer_id.in.(${custIds.join(',')}),invoice_to_id.in.(${custIds.join(',')}),consignor_id.in.(${custIds.join(',')}),hbl_importer_id.in.(${custIds.join(',')}),hbl_consignor_id.in.(${custIds.join(',')})`
        )
      : Promise.resolve({ data: [] as { id: string }[] }),
  ])
  childrenRes.data?.forEach((j: { id: string }) => idSet.add(j.id))
  custJobsRes.data?.forEach((j: { id: string }) => idSet.add(j.id))

  return Array.from(idSet)
}

function FilterPills({ label, options, value, onChange }: {
  label: string
  options: readonly { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${
              value === opt.value
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const DEFAULT_FILTERS: Record<string, any> = { hide_sub_bills: true }

export default function Jobs() {
  const navigate = useNavigate()
  const { user, userEmail, userRoles } = useAuth()
  // Sub-bills (Consol houses, group shipments) are hidden by default: the list shows masters and groups; the checkbox shows everything.
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_FILTERS)
  const [isNewGroupOpen, setIsNewGroupOpen] = useState(false)
  // Z9-fix2: search now fans out to ~8 queries (see searchMatchingJobIds) instead of one --
  // debounce the text box specifically so typing doesn't fire that on every keystroke.
  const [searchInput, setSearchInput] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isPreReserveOpen, setIsPreReserveOpen] = useState(false)
  const [jobs, setJobs] = useState<any[]>([])
  const [jobsTotal, setJobsTotal] = useState<number | null>(null)
  const { page, setPage, pageSize, setPageSize, range } = usePagination('jobs-list')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmSync, setConfirmSync] = useState<{ job: any } | null>(null)
  const [duplicateJobId, setDuplicateJobId] = useState<string | null>(null)
  // Z58.7: every sync click opens the consolidated Sync Preflight modal FIRST (InvoiceTo +
  // Shipper/Consignee/Notify, all with a visible status) -- replaces the old reactive two-stage
  // flow (call sync, get blocked, show one dialog at a time), which had no way to show a party
  // that never gets blocked at all (e.g. Consignee/Notify sharing InvoiceTo's own BoxTop account).
  const [pendingPreflight, setPendingPreflight] = useState<{ job: any; force: boolean } | null>(null)
  // Read-back check: what BoxTop actually stored for a job (after a sync, or on demand).
  const [readbackView, setReadbackView] = useState<{ jobRef: string; readback: BoxtopReadback } | null>(null)
  const [readbackBusy, setReadbackBusy] = useState<string | null>(null)
  const [portProblem, setPortProblem] = useState<{ jobRef: string; problems: PortProblem[] } | null>(null)
  const [missingAccounts, setMissingAccounts] = useState<{ jobRef: string; parties: MissingAccountParty[] } | null>(null)

  // Z36: Active Reservations panel (finishing Z31 -- the table + Pre-reserve create flow
  // shipped there, this is the view/release/use side).
  const [activeReservations, setActiveReservations] = useState<any[]>([])
  // job_ref_reservations rows with status='used' are what a job's ref was originally reserved
  // as -- keyed by job_id (not job_ref) so a badge survives a later job_ref rename (Z30).
  const [usedReservationJobIds, setUsedReservationJobIds] = useState<Set<string>>(new Set())
  const [reservationsExpanded, setReservationsExpanded] = useState(true)

  const loadReservations = async () => {
    const [activeRes, usedRes] = await Promise.all([
      supabase.from('job_ref_reservations').select('id, job_ref, reserved_by, reserved_at, note').eq('status', 'active').order('reserved_at', { ascending: true }),
      supabase.from('job_ref_reservations').select('job_id').eq('status', 'used').not('job_id', 'is', null),
    ])
    setActiveReservations(activeRes.data || [])
    setUsedReservationJobIds(new Set((usedRes.data || []).map((r: any) => r.job_id as string)))
  }

  useEffect(() => {
    loadReservations()
  }, [])

  // Z36 redo: release goes through release_job_ref() (server checks owner/ADMIN/OPERATIONS and that
  // the reservation is still active); errors are shown, not swallowed.
  const [reservationError, setReservationError] = useState<string | null>(null)
  const handleReleaseReservation = async (id: string) => {
    setReservationError(null)
    const { error } = await supabase.rpc('release_job_ref', { p_id: id })
    if (error) setReservationError(error.message)
    loadReservations()
  }

  // "Use this" opens the New Job dialog with the reserved number locked in (manual entry by default,
  // AutoJobs as an option there). Someone else's reservation: ask first, then allow.
  const [useReservation, setUseReservation] = useState<{ id: string; job_ref: string } | null>(null)
  const handleUseReservation = (r: { id: string; job_ref: string; reserved_by: string }) => {
    if (r.reserved_by !== userEmail && !window.confirm(`${r.job_ref} was reserved by ${r.reserved_by}. Use it anyway?`)) return
    setUseReservation({ id: r.id, job_ref: r.job_ref })
  }

  const { sync, isSyncing, readbackJob } = useSyncToBoxtrax()

  // Y4: column visibility/order, persisted per-user (same pattern as the Warehouse
  // module's list pages -- see src/pages/Warehouse/useColumnPrefs.ts).
  const { order: colOrder, hidden: colHidden, visibleCols, setOrder: setColOrder, toggleHidden, reset: resetCols } =
    useColumnPrefs('col_pref_jobs_list', JOBS_COL_DEFS, user?.id, undefined, undefined, DEFAULT_HIDDEN_JOBS_COLS)
  const colSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handleColDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oi = colOrder.indexOf(active.id as string)
    const ni = colOrder.indexOf(over.id as string)
    if (oi !== -1 && ni !== -1) setColOrder(arrayMove(colOrder, oi, ni))
  }

  // Z34: with this many columns the table's own horizontal scrollbar sits at the bottom of a
  // (often very tall) table -- reaching it means scrolling the whole page down first. Mirror it
  // as a thin bar fixed to the bottom of the viewport (see the JSX below for why fixed, not
  // sticky) whose scroll position is kept in sync with the real table wrapper in both
  // directions.
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const scrollMirrorRef = useRef<HTMLDivElement>(null)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [hasHScroll, setHasHScroll] = useState(false)

  useEffect(() => {
    const wrapper = tableScrollRef.current
    const table = tableRef.current
    if (!wrapper || !table) { setHasHScroll(false); return }
    const measure = () => {
      setScrollWidth(wrapper.scrollWidth)
      setHasHScroll(wrapper.scrollWidth > wrapper.clientWidth + 1)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(wrapper)
    ro.observe(table)
    return () => ro.disconnect()
  }, [jobs, colOrder, colHidden])

  const handleTableScroll = () => {
    if (scrollMirrorRef.current && tableScrollRef.current) {
      scrollMirrorRef.current.scrollLeft = tableScrollRef.current.scrollLeft
    }
  }
  const handleMirrorScroll = () => {
    if (tableScrollRef.current && scrollMirrorRef.current) {
      tableScrollRef.current.scrollLeft = scrollMirrorRef.current.scrollLeft
    }
  }

  useEffect(() => {
    const t = setTimeout(() => handleFilterChange('search', searchInput), 350)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setPage(0)
  }, [JSON.stringify(filters)])

  useEffect(() => {
    loadJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), page, pageSize])

  const loadJobs = async () => {
    try {
      setLoading(true)
      setError(null)

      // Z35.1: total count only needs to be re-queried once per 30s per distinct filter
      // set -- paging through the same filtered list doesn't need a fresh count(*) on
      // every click.
      const countCacheKey = `jobs-list:${JSON.stringify(filters)}`
      const cachedCount = getCachedCount(countCacheKey)

      // Z57: reverted the .eq('is_active', true) filter added for Z56.2 -- that column only
      // exists once 20260904g_z56_2_jobs_soft_delete_and_test_data_cleanup.sql has actually been
      // run in the Supabase SQL Editor (this migration only creates the .sql file, it doesn't
      // apply it), and filtering on a column that doesn't exist yet breaks this query with
      // "column jobs.is_active does not exist" -- exactly what took the whole Jobs page down.
      // Re-add this filter once that migration has been confirmed run.
      let query = supabase.from('jobs')
        .select(JOBS_SELECT, cachedCount === undefined ? { count: 'exact' } : undefined)
        .order('created_at', { ascending: false })

      if (filters.search) {
        const matchingIds = await searchMatchingJobIds(filters.search)
        if (matchingIds.length === 0) {
          setJobs([])
          setJobsTotal(0)
          setCachedCount(countCacheKey, 0)
          setLoading(false)
          return
        }
        query = query.in('id', matchingIds)
      }
      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      // Y3: My Jobs / Pre-Jobs / All
      if (filters.scope === 'mine' && userEmail) {
        query = query.or(`created_by.eq.${userEmail},assigned_to.eq.${userEmail}`)
      } else if (filters.scope === 'pre') {
        query = query.eq('status', 'PRE')
      }

      // Y3: Import/Export/Domestic/Cross Trade/All
      if (filters.direction_group === 'IMP' || filters.direction_group === 'EXP') {
        query = query.eq('direction', filters.direction_group)
      } else if (filters.direction_group === 'DOMESTIC') {
        query = query.eq('transport_mode', 'DOMESTIC')
      } else if (filters.direction_group === 'CROSS_TRADE') {
        query = query.eq('is_cross_trade', true)
      }

      // Y3: Air/Sea/Road/Rail/Courier/All
      if (filters.transport_mode) {
        query = query.eq('transport_mode', filters.transport_mode)
      }

      // Y3: Not Invoiced/Open Jobs/All
      // A group's shipments are never invoiced and never completed on their own: leave them out of these two views (they are folded into the group).
      if (filters.invoice_status === 'not_invoiced') {
        query = query.eq('is_invoiced', false).or('import_source.is.null,import_source.neq.GROUP')
      } else if (filters.invoice_status === 'open') {
        query = query.not('status', 'in', '(COM,CAN,INV)').or('import_source.is.null,import_source.neq.GROUP')
      }

      // Y5: Hide sub-bills / Show only masters
      // A search term overrides it: a shipment found by its airway bill or reference must show, even though it is normally folded into its group.
      if (filters.hide_sub_bills && !filters.search) {
        query = query.is('parent_job_id', null)
      }

      if (range) query = query.range(range[0], range[1])

      const { data, error: err, count } = await query

      if (err) throw err
      setJobs(data || [])
      if (cachedCount !== undefined) {
        setJobsTotal(cachedCount)
      } else {
        setJobsTotal(count ?? 0)
        setCachedCount(countCacheKey, count ?? 0)
      }
    } catch (err) {
      console.error('Jobs fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
      setJobs([])
      setJobsTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const refreshJob = async (jobId: string) => {
    const { data } = await supabase.from('jobs').select(JOBS_SELECT).eq('id', jobId).single()
    if (data) {
      setJobs(prev => prev.map(j => j.id === jobId ? data : j))
    }
  }

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Z58.7: opens the Sync Preflight modal -- the actual sync call only happens after staff
  // approves it (handleSyncAfterPreflight below).
  const handleSync = (job: any, force = false) => {
    setPendingPreflight({ job, force })
  }

  const handleSyncAfterPreflight = async (job: any, force: boolean) => {
    const result = await sync(job.job_ref, { force, invoiceToConfirmed: true })
    if (result.result === 'invoice_to_not_bound') {
      showToast(result.error || 'Customer has no BoxTop account bound', 'error')
      return
    }
    if ((result.result === 'invoice_to_address_mismatch' || result.result === 'ambiguous_contact_match')) {
      // Rare race: BoxTop's data changed between the preflight check and this call. Surface it
      // rather than silently retrying -- staff already approved a preflight that's now stale.
      showToast('BoxTop data changed since preflight was approved — please retry sync', 'error')
      return
    }
    if (result.result === 'address_not_linked') {
      showToast(result.error || 'Link the Shipper and Consignee addresses in the Sync Preflight before syncing', 'error')
      return
    }
    if (result.result === 'address_not_chosen') {
      showToast(result.error || 'Choose the address for each party on the job before syncing', 'error')
      return
    }
    if (result.result === 'missing_boxtop_account' && result.missingParties?.length) {
      setMissingAccounts({ jobRef: job.job_ref, parties: result.missingParties })
      return
    }
    if (result.result === 'port_code_missing' && result.portProblems) {
      setPortProblem({ jobRef: job.job_ref, problems: result.portProblems })
      return
    }
    if (result.alreadyExists) {
      // KI26034 P0: idempotency guard blocked this -- BoxTop already has a
      // JobId for this job and there's no update endpoint, so nothing was
      // sent to BoxTop.
      showToast(`Already exists in BoxTop (JobId=${result.boxtraxJobId}) — not re-sent`, 'success')
    } else if (result.partial) {
      // KI26034 P0: BoxTop created the job but returned no recognized
      // reference field -- not a failure, just unconfirmed.
      showToast('Synced (pending reference) — check with BoxTop support', 'success')
    } else if (result.success) {
      showToast(`Synced to BoxTop: ${result.boxtraxRef}`, 'success')
      if (result.readback) setReadbackView({ jobRef: job.job_ref, readback: result.readback })
    } else {
      showToast(result.error || 'Sync failed', 'error')
    }
    await refreshJob(job.id)
  }

  const handleReadback = async (job: any) => {
    setReadbackBusy(job.job_ref)
    const r = await readbackJob(job.job_ref)
    setReadbackBusy(null)
    if (r.success && r.readback) setReadbackView({ jobRef: job.job_ref, readback: r.readback })
    else showToast(r.error || 'Could not read the job back from BoxTop', 'error')
  }

  const handleFilterChange = (key: string, value: string | boolean) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }))
  }

  const clearFilters = () => {
    setSearchInput('')
    setFilters(DEFAULT_FILTERS)
  }

  const getStatusColor = (status: string) => {
    const colors = {
      PRE: 'bg-gray-100 text-gray-800',
      CUS: 'bg-blue-100 text-blue-800',
      CTR: 'bg-indigo-100 text-indigo-800',
      DEL: 'bg-green-100 text-green-800',
      PDY: 'bg-yellow-100 text-yellow-800',
      POD: 'bg-green-200 text-green-900',
      INV: 'bg-purple-100 text-purple-800',
      WAR: 'bg-orange-100 text-orange-800',
      RET: 'bg-orange-100 text-orange-800',
      COM: 'bg-green-100 text-green-800',
      PRO: 'bg-blue-200 text-blue-900',
      CAN: 'bg-red-100 text-red-800',
      DEF: 'bg-gray-200 text-gray-900',
      WKD: 'bg-cyan-100 text-cyan-800',
      EXP: 'bg-red-200 text-red-900',
      SDI: 'bg-yellow-200 text-yellow-900',
      REQ: 'bg-blue-100 text-blue-800'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const getSyncStatusBadge = (syncStatus: string | null) => {
    if (!syncStatus) return <span className="text-gray-400">-</span>
    const styles = {
      synced: 'bg-green-100 text-green-800',
      pending: 'bg-gray-100 text-gray-700',
      failed: 'bg-red-100 text-red-800'
    }
    const cls = styles[syncStatus as keyof typeof styles] || 'bg-gray-100 text-gray-700'
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
        {syncStatus}
      </span>
    )
  }

  const td = 'px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Jobs Management
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage freight forwarding jobs (MBL/HBL) and shipment tracking
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/jobs/autojobs')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              AutoJobs
            </button>
            <button
              onClick={() => navigate('/jobs/autojobs/templates')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Document Templates
            </button>
            <button
              onClick={() => setIsPreReserveOpen(true)}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Pre-reserve
            </button>
            <button
              onClick={() => setIsNewGroupOpen(true)}
              className="px-6 py-3 border border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium transition-colors"
              title="A customer's shipments for a period under one reference, one invoice"
            >
              New Group
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Job
            </button>
          </div>
        </div>

        {/* Y3: Top filter groups */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-4">
          <div className="flex flex-wrap gap-6">
            <FilterPills label="Scope" options={SCOPE_OPTIONS} value={filters.scope || ''} onChange={v => handleFilterChange('scope', v)} />
            <FilterPills label="Direction" options={DIRECTION_GROUP_OPTIONS} value={filters.direction_group || ''} onChange={v => handleFilterChange('direction_group', v)} />
            <FilterPills label="Mode" options={MODE_OPTIONS} value={filters.transport_mode || ''} onChange={v => handleFilterChange('transport_mode', v)} />
            <FilterPills label="Invoicing" options={INVOICE_OPTIONS} value={filters.invoice_status || ''} onChange={v => handleFilterChange('invoice_status', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Sub-bills</label>
              <label className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!filters.hide_sub_bills}
                  onChange={e => handleFilterChange('hide_sub_bills', e.target.checked)}
                  className="rounded"
                />
                Hide sub-bills / masters only
              </label>
            </div>
          </div>
        </div>

        {/* Search / Status / Column picker */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search job ref, customer, container, BL/AWB, vessel..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                {STATUSES.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear Filters
            </button>

            <div className="ml-auto">
              <ColumnToggleButton cols={JOBS_COL_DEFS} hidden={colHidden} onToggle={toggleHidden} onReset={resetCols} />
            </div>
          </div>
        </div>

        {/* Z36: Active Reservations -- who's holding which KI/KE number ahead of the job
            actually existing, and why. Expanded by default so it's discoverable. */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => setReservationsExpanded(v => !v)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <span className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Active Reservations
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({activeReservations.length})</span>
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${reservationsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {reservationError && <p className="px-6 py-2 text-sm text-red-600 border-t border-gray-200 dark:border-gray-700">{reservationError}</p>}
          {reservationsExpanded && (
            activeReservations.length === 0 ? (
              <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                No active reservations.
              </div>
            ) : (
              <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {['Job Ref', 'Reserved By', 'Reserved', 'Note', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {activeReservations.map(r => {
                      const canRelease = r.reserved_by === userEmail || hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])
                      return (
                        <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2.5 font-mono font-bold text-gray-900 dark:text-white whitespace-nowrap">{r.job_ref}</td>
                          <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{r.reserved_by}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap" title={new Date(r.reserved_at).toLocaleString()}>
                            {formatRelativeTime(r.reserved_at)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{r.note || '—'}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap space-x-3">
                            <button onClick={() => handleUseReservation(r)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium">
                              Use this
                            </button>
                            {canRelease && (
                              <button onClick={() => handleReleaseReservation(r.id)} className="text-red-600 dark:text-red-400 hover:underline text-xs font-medium">
                                Release
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Jobs List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-300">Loading jobs...</p>
            </div>
          ) : error ? (
            <div className="p-8">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="mt-4 text-lg font-medium text-yellow-800 dark:text-yellow-300">Unable to load jobs</p>
                <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">{error}</p>
              </div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-8">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No jobs yet</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {Object.values(filters).some(v => v)
                    ? 'No jobs found matching the current filters. Try adjusting your search criteria.'
                    : 'Get started by creating your first job or waiting for data to be added.'}
                </p>
                <button
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create New Job
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Z35.1: controls at both top and bottom of the list */}
              <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={jobsTotal} />
              <div ref={tableScrollRef} onScroll={handleTableScroll} className="overflow-x-auto">
              <table ref={tableRef} className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <DndContext sensors={colSensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
                  <SortableContext items={visibleCols.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {visibleCols.map(col => (
                          <SortableColHeader
                            key={col.id}
                            id={col.id}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                          >
                            {col.label}
                          </SortableColHeader>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                  </SortableContext>
                </DndContext>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {jobs.map((job) => {
                    const sea = firstOrSelf(job.job_sea_details)
                    const air = firstOrSelf(job.job_air_details)
                    const terms = sea?.shipping_terms?.code || air?.shipping_terms?.code || null
                    const originPort = sea?.departure_port || air?.departure_port || null
                    const destPort = sea?.arrival_port || air?.arrival_port || null
                    const eta = air?.arrival_date || sea?.arrival_date || null
                    const arrivalDate = air?.actual_arrival_date || sea?.arrival_date || null
                    // Z71 Bug 1: mawb_no already stores the FULL waybill number including its
                    // IATA prefix (create-autojobs writes master.waybill_number/detail.mbl_number
                    // verbatim into mawb_no, and ShipDetailsTab's manual-entry form does the
                    // same) -- mawb_prefix is a separate, independently-derived field used only
                    // for auto-filling the Airline dropdown (see ShipDetailsTab's
                    // handleMawbChange), not a piece to reassemble mawb_no from. Prepending it
                    // here produced a duplicated prefix, e.g. "618-618-57502384".
                    const awbMaster = sea?.master_bl_no || air?.mawb_no || null
                    const goods: any[] = job.job_goods || []
                    const nop = goods.reduce((s, g) => s + (g.no_of_packs || 0), 0)
                    const weight = goods.reduce((s, g) => s + (g.gross_weight || 0), 0)
                    const chargeable = goods.reduce((s, g) => s + (g.chargeable_weight_hawb ?? g.chargeable_weight_mawb ?? 0), 0)
                    // Z12-fix1: for a CONSOL child (HBL) job, importer_id/consignor_id mirror
                    // the PARENT MBL's parties (see K9 in create-autojobs) -- the child's own
                    // party lives on hbl_importer_id/hbl_consignor_id instead. Prefer those here
                    // so list/search/BoxTop show the HBL's actual consignee, not the parent's.
                    const isConsolChild = !!job.parent_job_id
                    const rowImporterName = (isConsolChild ? job.hbl_importer?.name : null) || job.importer?.name
                    const rowConsignorName = (isConsolChild ? job.hbl_consignor?.name : null) || job.consignor?.name
                    // Z71.3: reverses Z9-fix1 per Gary -- 'Customer' is now the paying/billing
                    // party (invoice_to_id, e.g. a forwarder like BNJ/LANDR), not the importer/
                    // consignee. invoice_to_id lives directly on jobs (no hbl_-level override the
                    // way importer/consignor have for CONSOL children), so no isConsolChild
                    // branching is needed here. The importer/consignee is still visible via its
                    // own (default-hidden) "Consignee Name" column below. Null falls back to '—'
                    // rather than showing the wrong party.
                    const customerName = job.invoice_to?.name || '—'
                    const jobTypeLabel = job.is_cross_trade ? 'Cross Trade' : job.transport_mode === 'DOMESTIC' ? 'Domestic' : job.direction === 'IMP' ? 'Import' : 'Export'

                    const cells: Record<string, React.ReactNode> = {
                      job_ref: (
                        <td key="job_ref" className="px-4 py-3 whitespace-nowrap text-sm font-mono font-bold text-blue-600 dark:text-blue-400">
                          <div className="flex items-center gap-2">
                            <span>{job.job_ref}</span>
                            {BOXTOP_SYNC_ENABLED && job.import_source === 'AUTOJOBS' && (
                              <div className={`w-2.5 h-2.5 rounded-full ${job.boxtrax_synced ? 'bg-green-500' : 'bg-orange-500'}`} title={job.boxtrax_synced ? 'Synced to Boxtrax' : 'Pending Boxtrax sync'} />
                            )}
                            {/* Z36: this job's ref started life as a pre-reservation */}
                            {usedReservationJobIds.has(job.id) && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300" title="This job's reference was pre-reserved before the job was created">
                                Reserved
                              </span>
                            )}
                          </div>
                        </td>
                      ),
                      customer_name: <td key="customer_name" className={td}>{customerName}</td>,
                      origin_port: <td key="origin_port" className={td}>{fmtPort(originPort)}</td>,
                      destination_port: <td key="destination_port" className={td}>{fmtPort(destPort)}</td>,
                      terms: <td key="terms" className={td}>{terms || '—'}</td>,
                      eta: <td key="eta" className={td}>{fmtDate(eta)}</td>,
                      job_date: <td key="job_date" className={td}>{fmtDate(job.booked_date)}</td>,
                      service_type: <td key="service_type" className={td}>{job.job_type}</td>,
                      master_job: <td key="master_job" className={td}>{job.parent?.job_ref || '—'}</td>,
                      customer_ref: <td key="customer_ref" className={td}>{job.client_ref || '—'}</td>,
                      job_direction: <td key="job_direction" className={td}>{jobTypeLabel}</td>,
                      transport_mode: <td key="transport_mode" className={td}>{job.transport_mode}</td>,
                      awb_master: <td key="awb_master" className={`${td} font-mono`}>{awbMaster || '—'}</td>,
                      arrival_date: <td key="arrival_date" className={td}>{fmtDate(arrivalDate)}</td>,
                      complete: <td key="complete" className={td}>{job.status === 'COM' ? 'Yes' : 'No'}</td>,
                      shipper_name: <td key="shipper_name" className={td}>{rowConsignorName || '—'}</td>,
                      consignee_name: <td key="consignee_name" className={td}>{rowImporterName || '—'}</td>,
                      nop: <td key="nop" className={td}>{nop || '—'}</td>,
                      weight: <td key="weight" className={td}>{weight ? weight.toFixed(1) : '—'}</td>,
                      chargeable_weight: <td key="chargeable_weight" className={td}>{chargeable ? chargeable.toFixed(1) : '—'}</td>,
                      status: (
                        <td key="status" className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </td>
                      ),
                      sync_status: <td key="sync_status" className="px-4 py-3 whitespace-nowrap">{getSyncStatusBadge(job.sync_status)}</td>,
                      boxtrax_ref: (
                        <td key="boxtrax_ref" className={`${td} font-mono`}>
                          {job.boxtrax_ref ? (
                            <div className="flex items-center gap-1.5">
                              {job.boxtrax_ref.startsWith('KII') && (
                                <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" title="Synced to Boxtrax" />
                              )}
                              <span>{job.boxtrax_ref}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      ),
                    }

                    return (
                      <tr
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        {visibleCols.map(col => cells[col.id])}
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium cursor-pointer"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              Open →
                            </span>
                            {job.job_type !== 'CONSOL' && job.job_type !== 'GROUP' && (
                              <button
                                onClick={() => setDuplicateJobId(job.id)}
                                title="Duplicate this job"
                                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
                              >
                                Copy
                              </button>
                            )}
                            {BOXTOP_SYNC_ENABLED && (<>
                            {job.boxtrax_ref && (
                              <button
                                disabled={readbackBusy === job.job_ref}
                                onClick={() => handleReadback(job)}
                                title="Read this job back from BoxTop and compare what it stored with what we sent (read-only)"
                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline disabled:opacity-50"
                              >
                                {readbackBusy === job.job_ref ? 'Checking…' : 'Check in BoxTop'}
                              </button>
                            )}
                            {job.boxtrax_ref ? (
                              <button
                                disabled={isSyncing(job.job_ref)}
                                onClick={() => setConfirmSync({ job })}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                              >
                                {isSyncing(job.job_ref) ? (
                                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                  </svg>
                                ) : null}
                                Re-sync
                              </button>
                            ) : job.boxtrax_job_id ? (
                              // KI26034 P0: 'partial' outcome -- BoxTop has a JobId for this
                              // job but we never got a usable reference back, so boxtrax_ref
                              // is still null. No working update endpoint exists, so this is
                              // NOT a plain "Sync" button -- that would create yet another
                              // duplicate in BoxTop. Force re-sync is opt-in and explicit.
                              <span className="inline-flex items-center gap-2 text-xs">
                                <span className="text-gray-500 dark:text-gray-400" title="BoxTop already has a record for this job -- no update endpoint available to refresh it">
                                  BoxTop exists (JobId={job.boxtrax_job_id})
                                </span>
                                <button
                                  disabled={isSyncing(job.job_ref)}
                                  onClick={() => setConfirmSync({ job })}
                                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Force re-sync
                                </button>
                              </span>
                            ) : (
                              <button
                                disabled={isSyncing(job.job_ref)}
                                onClick={() => handleSync(job)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isSyncing(job.job_ref) ? (
                                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                  </svg>
                                ) : null}
                                Sync
                              </button>
                            )}
                            </>)}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={jobsTotal} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Z34: mirrored horizontal scrollbar, fixed to the bottom of the viewport so it's
          reachable at a glance without scrolling the page down to the table's own bottom edge
          first -- a wide table can run to hundreds of rows. position: sticky doesn't work here:
          placed after a tall table it only starts "sticking" once you're already nearly at the
          bottom of the page (confirmed by hand before landing this), since its static flow
          position is what sticky measures from. Fixed positioning sidesteps that entirely. Only
          rendered when the table actually overflows -- on a wide viewport, or with columns
          hidden via the column picker, there may be nothing to scroll. */}
      {hasHScroll && (
        <div className="fixed bottom-0 left-0 right-0 z-20 pointer-events-none">
          <div className="container mx-auto px-4">
            <div
              ref={scrollMirrorRef}
              onScroll={handleMirrorScroll}
              className="pointer-events-auto overflow-x-auto overflow-y-hidden h-3 bg-gray-100 dark:bg-gray-900 border border-b-0 border-gray-200 dark:border-gray-700 rounded-t-lg shadow-[0_-2px_6px_rgba(0,0,0,0.08)]"
              aria-hidden="true"
            >
              <div style={{ width: scrollWidth }} className="h-px" />
            </div>
          </div>
        </div>
      )}

      {isNewGroupOpen && <NewGroupDialog onClose={() => setIsNewGroupOpen(false)} />}
      {/* Create Job Modal */}
      <CreateJobModal isOpen={isCreateModalOpen || !!useReservation} reservation={useReservation} onClose={() => { setIsCreateModalOpen(false); setUseReservation(null) }} />
      {isPreReserveOpen && <PreReserveDialog onClose={() => setIsPreReserveOpen(false)} onReserved={loadReservations} />}

      {/* Z24: BoxTop Contact match confirm, shown when a sync is blocked on an ambiguous match */}
      {/* Z58.7: consolidated Sync Preflight -- InvoiceTo/Shipper/Consignee/Notify all shown,
          every party has a visible status, "Approve All & Sync" only enabled once every
          needs_decision party is resolved. */}
      {BOXTOP_SYNC_ENABLED && portProblem && (
        <PortCodeProblemDialog jobRef={portProblem.jobRef} problems={portProblem.problems} onClose={() => setPortProblem(null)} />
      )}
      {BOXTOP_SYNC_ENABLED && missingAccounts && (
        <MissingBoxtopAccountDialog jobRef={missingAccounts.jobRef} parties={missingAccounts.parties} onClose={() => setMissingAccounts(null)} />
      )}
      {BOXTOP_SYNC_ENABLED && readbackView && (
        <BoxtopReadbackDialog jobRef={readbackView.jobRef} readback={readbackView.readback} onClose={() => setReadbackView(null)} />
      )}
      {BOXTOP_SYNC_ENABLED && pendingPreflight && (
        <SyncPreflightModal
          jobRef={pendingPreflight.job.job_ref}
          onCancel={() => setPendingPreflight(null)}
          onApproved={() => {
            const { job, force } = pendingPreflight
            setPendingPreflight(null)
            handleSyncAfterPreflight(job, force)
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? (
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Re-sync Confirm Dialog */}
      {BOXTOP_SYNC_ENABLED && confirmSync && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Confirm Re-sync</h3>
            {confirmSync.job.boxtrax_ref ? (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Already synced <span className="font-mono font-bold">{confirmSync.job.boxtrax_ref}</span>. Resend?
              </p>
            ) : (
              // KI26034 P0: no update endpoint exists, so forcing here does not refresh
              // the existing BoxTop record -- it creates a brand-new one alongside it.
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                BoxTop already has JobId=<span className="font-mono font-bold">{confirmSync.job.boxtrax_job_id}</span>.
                No update endpoint exists — forcing re-sync will <span className="font-bold text-red-600 dark:text-red-400">create a new duplicate record</span> in BoxTop. Continue?
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmSync(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const job = confirmSync.job
                  setConfirmSync(null)
                  handleSync(job, true)
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {duplicateJobId && (
        <DuplicateJobModal sourceJobId={duplicateJobId} onClose={() => setDuplicateJobId(null)} />
      )}
    </div>
  )
}
